import React, { useEffect, useState } from 'react';
import type { ESignPackage, Participant } from '../types/docmagic';
import type { BusyAction } from '../hooks/usePackages';
import { PackageHeader } from './PackageHeader';
import { PackageAlerts } from './PackageAlerts';
import { DetailsTab } from './tabs/DetailsTab';
import { DocumentsReceivedTab, DocumentsTab, EmailsTab, VersionsTab } from './tabs/SecondaryTabs';
type TabId = 'details' | 'documents' | 'received' | 'emails' | 'versions';
interface ESignPackagePanelProps {
  pkg: ESignPackage;
  busy?: BusyAction;
  onSync: () => void;
  onRetryPdf: () => void;
  onSendEmail: (p: Participant) => void;
  onSign: (p: Participant) => void;
}
export function ESignPackagePanel({
  pkg,
  busy,
  onSync,
  onRetryPdf,
  onSendEmail,
  onSign
}: ESignPackagePanelProps) {
  const [tab, setTab] = useState<TabId>('details');
  useEffect(() => setTab('details'), [pkg.id]);
  const tabs: {
    id: TabId;
    label: string;
    count?: number;
  }[] = [{
    id: 'details',
    label: 'Details'
  }, {
    id: 'documents',
    label: 'Documents',
    count: pkg.documents.length
  }, {
    id: 'received',
    label: 'Documents Received',
    count: pkg.receivedDocuments.length
  }, {
    id: 'emails',
    label: 'Emails',
    count: pkg.emails.length
  }, {
    id: 'versions',
    label: 'Versions',
    count: pkg.versions.length
  }];
  return <article className="min-w-0 rounded border border-line bg-white shadow-sm" data-id="element-80" data-mp="8j3xnj76zsyj">
      <PackageHeader pkg={pkg} syncing={busy === 'sync'} onSync={onSync} data-id="element-81" data-mp="3przjkp23t5ml" />
      <PackageAlerts pkg={pkg} busy={busy} onSync={onSync} onRetryPdf={onRetryPdf} data-id="element-82" data-mp="3urc618p2caon" />

      <div role="tablist" aria-label="Package sections" className="mt-2 flex overflow-x-auto border-b border-line px-4" data-id="element-83" data-mp="3uozeq4xf0nyx">
        {tabs.map(t => {
        const active = tab === t.id;
        return <button key={t.id} role="tab" id={`tab-${t.id}`} aria-selected={active} aria-controls={`panel-${t.id}`} onClick={() => setTab(t.id)} className={`-mb-px whitespace-nowrap border-b-[3px] px-3 py-2.5 text-sm focus:outline-none focus-visible:bg-canvas ${active ? 'border-brand font-semibold text-ink' : 'border-transparent text-muted hover:text-ink'}`} data-id="element-84" data-mp="1f07rsw7ixakx">
              {t.label}
              {t.count !== undefined && <span className="ml-1 text-muted" data-id="element-85" data-mp="13mfik1tjzank">({t.count})</span>}
            </button>;
      })}
      </div>

      <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="p-4" data-id="element-86" data-mp="21apm4l8zm6gs">
        {tab === 'details' && <DetailsTab pkg={pkg} busy={busy} onRetryPdf={onRetryPdf} onSendEmail={onSendEmail} onSign={onSign} data-id="element-87" data-mp="1rijyfnprqb7e" />}
        {tab === 'documents' && <DocumentsTab rows={pkg.documents} data-id="element-88" data-mp="1mj7b71uzj24a" />}
        {tab === 'received' && <DocumentsReceivedTab rows={pkg.receivedDocuments} data-id="element-89" data-mp="2jze4p0jzzzpe" />}
        {tab === 'emails' && <EmailsTab rows={pkg.emails} data-id="element-90" data-mp="2f8nyjl3qzstn" />}
        {tab === 'versions' && <VersionsTab rows={pkg.versions} data-id="element-91" data-mp="2f7321pxvhzt5" />}
      </div>
    </article>;
}